import { type Dispatch, type SetStateAction, useCallback, useState } from 'react';

/**
 * A `useState` whose value is persisted to localStorage under `key`. Lazily reads
 * the stored value on mount and writes on every change — mirrors the try/catch
 * pattern in `src/context/theme-context.tsx`. Used for durable UI preferences (e.g.
 * the selected list view mode) so they survive reloads and are shared across screens.
 * Falls back to `fallback` when storage is unavailable or holds an invalid value.
 */
export const usePersistentState = <T extends string>(key: string, fallback: T): [T, Dispatch<SetStateAction<T>>] => {
	const [value, setValue] = useState<T>(() => {
		try {
			const stored = localStorage.getItem(key);
			return stored != null ? (stored as T) : fallback;
		} catch {
			return fallback;
		}
	});

	const set = useCallback<Dispatch<SetStateAction<T>>>(
		(next) => {
			setValue((prev) => {
				const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
				try {
					localStorage.setItem(key, resolved);
				} catch {
					// Storage unavailable (private mode / quota) — keep the in-memory value.
				}
				return resolved;
			});
		},
		[key]
	);

	return [value, set];
};
